'use client';

import type { PayInResponse } from '@payments/aurum-contracts';
import { API_ROUTES, useApiMutation, useApiService } from '@shared/api-utils';
import { createDebug } from '@shared/debug';
import { useBackgroundPolling, useLongPoll } from '@shared/hooks';
import { useClientConfig } from '@shared/utils';
import { useTranslations } from 'next-intl';
import { type MutableRefObject, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { getPayInErrorMessage, translateAurumErrorKey } from '../../utils/aurumErrorTranslation';
import {
  type DepositErrorHandler,
  type DepositTransactionPayload,
  type TransactionRedirectionResponse,
  type TransactionStatusResponse,
  type TransactionStatusState,
  DEPOSIT_ERROR_STEP,
  TRANSACTION_STATUS_FAILURE_STATES,
  TRANSACTION_STATUS_SUCCESS_STATES,
} from '../types';
import { getLoadingWindowContent } from './useDepositFlow.utils';

const REDIRECTION_POLLING_INTERVAL_MS = 500;
const TRANSACTION_STATUS_POLLING_INTERVAL_MS = 500;

const isTransactionStatusSuccessState = (state: TransactionStatusState) => (TRANSACTION_STATUS_SUCCESS_STATES as ReadonlyArray<TransactionStatusState>).includes(state);

const isTransactionStatusFailureState = (state: TransactionStatusState) => (TRANSACTION_STATUS_FAILURE_STATES as ReadonlyArray<TransactionStatusState>).includes(state);

function getPayInErrorKey(response?: PayInResponse | null): string | undefined {
  return response?.errors?.errorKeys?.[0]?.translationKey;
}

type TransactionStatusLongPollPayload = Omit<TransactionStatusResponse, 'isFirstPayIn'> & {
  isFirstPayin: boolean;
};

/**
 * Retry function for React Query that doesn't retry on HTTP 4xx/5xx errors,
 * but allows retries for network errors to keep polling alive.
 *
 * This is useful for long-polling scenarios where we want to:
 * - Stop polling immediately on real API errors (4xx/5xx)
 * - Continue polling on network errors (timeouts, connection issues, etc.)
 *
 * @param failureCount - The number of times the query has failed (unused but required by React Query)
 * @param error - The error that occurred
 * @returns true to retry, false to stop retrying
 */
const retryOnNetworkErrorsOnly = (failureCount: number, error: unknown): boolean => {
  // Check if error is ApiError with status 400-599
  if (error && typeof error === 'object' && 'getStatus' in error && typeof (error as { getStatus: () => number }).getStatus === 'function') {
    try {
      const status = (error as { getStatus: () => number }).getStatus();
      // Don't retry on HTTP 4xx/5xx errors - these are real API errors that should be handled immediately
      if (status >= 400 && status < 600) {
        return false;
      }
    } catch {
      // If getStatus throws, treat as network error and retry
    }
  }
  // Retry on network errors and other errors to keep polling alive
  return true;
};

type UseDepositFlowOptions = {
  namespace: string;
  onError: DepositErrorHandler;
  onSuccess: (transactionStatus: TransactionStatusResponse) => void;
  onTransactionCreated?: (transactionId: string) => void;
  isTimedOut?: boolean;
};

type UseDepositFlowReturn = {
  createDepositTransactionMutation: ReturnType<typeof useApiMutation<PayInResponse, DepositTransactionPayload>>;
  transactionRedirectionCheckData: TransactionRedirectionResponse;
  transactionRedirectionCheckHeaders: Headers | undefined;
  transactionRedirectionCheckStatus: number | undefined;
  compoundId: string | null;
  isTransactionInProgress: boolean;
  isRedirectionInProgress: boolean;
  transactionStatusData: TransactionStatusResponse | undefined;
  depositType: 'FTD' | 'SD' | undefined;
  resetFlow: () => void;
  newWindowRef: MutableRefObject<Window | null>;
  openNewTab: () => void;
  closeNewTab: () => void;
};

function useDepositFlow({ namespace, onError, onSuccess, onTransactionCreated, isTimedOut = false }: UseDepositFlowOptions): UseDepositFlowReturn {
  const debug = createDebug({ isEnabled: false });
  const t = useTranslations(namespace);
  const [compoundId, setCompoundId] = useState<string | null>(null);
  const newWindowRef = useRef<Window | null>(null);
  // Use refs to track state changes immediately for polling control
  const isRedirectionCheckedRef = useRef(false);
  const isTransactionStatusCheckedRef = useRef(false);
  const [lastResolvedDepositType, setLastResolvedDepositType] = useState<'FTD' | 'SD' | undefined>(undefined);
  const INIT_REDIRECTION_CHECK_DATA = useMemo<TransactionRedirectionResponse>(
    () => ({
      payload: {
        redirection: {
          url: '',
          container: 'WINDOW',
          method: 'GET',
          postParams: {},
        },
      },
    }),
    []
  );
  const transactionRedirectionCheckDataRef = useRef<TransactionRedirectionResponse>(INIT_REDIRECTION_CHECK_DATA);

  const closeNewTab = useCallback(() => {
    if (newWindowRef.current && !newWindowRef.current.closed) {
      newWindowRef.current.close();
      newWindowRef.current = null;
    }
  }, []);

  const resetRedirectionCheckData = useCallback(() => {
    transactionRedirectionCheckDataRef.current = INIT_REDIRECTION_CHECK_DATA;
  }, [INIT_REDIRECTION_CHECK_DATA]);

  const emitProcessingError = useCallback(
    (errorMessage: string, transactionStatus?: TransactionStatusResponse, errorKey?: string) => {
      if (errorKey !== undefined) {
        onError(errorMessage, DEPOSIT_ERROR_STEP.PROCESSING_DEPOSIT as keyof typeof DEPOSIT_ERROR_STEP, transactionStatus, errorKey);
        return;
      }

      if (transactionStatus !== undefined) {
        onError(errorMessage, DEPOSIT_ERROR_STEP.PROCESSING_DEPOSIT as keyof typeof DEPOSIT_ERROR_STEP, transactionStatus);
        return;
      }

      onError(errorMessage, DEPOSIT_ERROR_STEP.PROCESSING_DEPOSIT as keyof typeof DEPOSIT_ERROR_STEP);
    },
    [onError]
  );

  const createDepositTransactionMutation = useApiMutation<PayInResponse, DepositTransactionPayload>(API_ROUTES.PAYMENTS.CREATE_DEPOSIT_TRANSACTION, 'POST', {
    requestOptions: {
      headers: {
        'Content-Type': 'application/json',
      },
    },
    mutationOptions: {
      onSuccess: (response: PayInResponse | null) => {
        if (response?.errors) {
          const errorKey = getPayInErrorKey(response);
          const errorMessage = getPayInErrorMessage(response.errors, t);
          closeNewTab();
          resetRedirectionCheckData();
          emitProcessingError(errorMessage || '', undefined, errorKey);
          return;
        }
        setCompoundId(response?.transactionId || null);
        onTransactionCreated?.(response?.transactionId || '');
      },
      onError: (error: unknown) => {
        const payInResponse = (error as { responseData?: PayInResponse }).responseData;
        const errorKey = getPayInErrorKey(payInResponse);
        const errorMessage = getPayInErrorMessage(payInResponse?.errors, t);
        closeNewTab();
        resetRedirectionCheckData();
        emitProcessingError(errorMessage || '', undefined, errorKey);
      },
    },
  });

  const apiService = useApiService();
  const { apiKey } = useClientConfig();

  const redirectionPollingEnabled = !!compoundId && !isRedirectionCheckedRef.current && !isTimedOut;
  const statusPollingEnabled = !!compoundId && isRedirectionCheckedRef.current && !isTransactionStatusCheckedRef.current && !isTimedOut;

  // State for redirection polling results
  const [transactionRedirectionCheckData, setTransactionRedirectionCheckData] = useState<TransactionRedirectionResponse | null>(null);
  const [transactionRedirectionCheckHeaders, setTransactionRedirectionCheckHeaders] = useState<Headers | undefined>(undefined);
  const [transactionRedirectionCheckStatus, setTransactionRedirectionCheckStatus] = useState<number | undefined>(undefined);
  const [transactionRedirectionCheckError, setTransactionRedirectionCheckError] = useState<Error | null>(null);

  // Redirection status check callback
  const onRedirectionStatusCheck = useCallback(async () => {
    if (!compoundId) return;

    const path = API_ROUTES.PAYMENTS.TRANSACTION_REDIRECTION_CHECK.replace(':compoundId', compoundId);
    const response = await apiService.get<TransactionRedirectionResponse>(path, {
      headers: apiKey ? { 'X-API-Key': apiKey } : undefined,
    });

    if (response.status === 304) {
      setTransactionRedirectionCheckError(null);
      setTransactionRedirectionCheckData(null);
      setTransactionRedirectionCheckStatus(304);
      return;
    }

    if (response.error) {
      setTransactionRedirectionCheckError(response.error);
      return;
    }

    setTransactionRedirectionCheckData(response.data);
    setTransactionRedirectionCheckHeaders(response.headers);
    setTransactionRedirectionCheckStatus(response.status);
  }, [compoundId, apiService, apiKey]);

  // Use background polling for redirection check
  useBackgroundPolling(onRedirectionStatusCheck, {
    baseInterval: REDIRECTION_POLLING_INTERVAL_MS,
    enabled: redirectionPollingEnabled,
  });

  // Update ref when data changes
  useEffect(() => {
    if (transactionRedirectionCheckData) {
      transactionRedirectionCheckDataRef.current = transactionRedirectionCheckData;
    }
  }, [transactionRedirectionCheckData]);

  const transactionStatusLongPollResult = useLongPoll<TransactionStatusLongPollPayload>(API_ROUTES.PAYMENTS.TRANSACTION_STATUS_CHECK.replace(':compoundId', compoundId || ''), {
    enabled: statusPollingEnabled,
    intervalMs: TRANSACTION_STATUS_POLLING_INTERVAL_MS,
    setDataTo: {
      state: '',
    },
    queryOptions: {
      retry: retryOnNetworkErrorsOnly,
    },
  });

  const { data: rawTransactionStatusData, error: transactionStatusError } = transactionStatusLongPollResult as {
    data: TransactionStatusLongPollPayload | null | undefined;
    error: Error | null;
  };

  const transactionStatusData = useMemo((): TransactionStatusResponse | undefined => {
    if (rawTransactionStatusData == null) return undefined;
    const { isFirstPayin, ...rest } = rawTransactionStatusData;
    return { ...rest, isFirstPayIn: isFirstPayin };
  }, [rawTransactionStatusData]);

  useEffect(() => {
    if (isRedirectionCheckedRef.current) return;

    // Handle errors from the redirection check API
    if (transactionRedirectionCheckError) {
      isRedirectionCheckedRef.current = true;
      emitProcessingError(t('aurumErrors.unknown'));
      return;
    }

    if (transactionRedirectionCheckStatus !== undefined) {
      if (transactionRedirectionCheckStatus === 304) {
        return;
      }

      // Handle error status codes (400, 500, etc.)
      if (transactionRedirectionCheckStatus >= 400) {
        isRedirectionCheckedRef.current = true;
        emitProcessingError(t('aurumErrors.unknown'));
        return;
      }

      // Stop polling for the transaction redirection and start polling for the transaction status
      isRedirectionCheckedRef.current = true;

      if (transactionRedirectionCheckStatus === 200) {
        // If data prop is missing (status 200 but no data returned), treat as error
        // Note: We check the prop, not the ref, because the ref always has a default value
        if (!transactionRedirectionCheckData) {
          emitProcessingError(t('aurumErrors.unknown'));
          return;
        }

        // If there's a redirection URL, open it in a new window (e.g., Pay by Bank flow)
        // Use prop data if available, otherwise fall back to ref (for backwards compatibility)
        const dataToCheck = transactionRedirectionCheckData || transactionRedirectionCheckDataRef.current;
        const redirectionUrl = dataToCheck?.payload?.redirection?.url;
        // If url is explicitly null (not empty string), treat as error
        if (redirectionUrl === null) {
          emitProcessingError(t('aurumErrors.unknown'));
          return;
        }
        if (redirectionUrl) {
          try {
            if (newWindowRef.current && !newWindowRef.current.closed) {
              // Use the pre-opened tab and redirect it to the URL
              newWindowRef.current.location.href = transactionRedirectionCheckDataRef.current.payload.redirection.url;
              newWindowRef.current.focus();
            } else {
              debug.forceError("Failed to redirect to the provider's page. The pre-opened tab is not available or has been closed.");
            }
            return;
          } catch (error) {
            debug.forceError("Failed to redirect to the provider's page. The pre-opened tab is not available or has been closed.", error);
          }
        }
      } else {
        closeNewTab();
        resetRedirectionCheckData();
      }

      return;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [transactionRedirectionCheckData, transactionRedirectionCheckStatus, transactionRedirectionCheckError, emitProcessingError, t, resetRedirectionCheckData, closeNewTab]);

  useEffect(() => {
    if (isTransactionStatusCheckedRef.current) return;

    // Don't handle errors if polling is not enabled (to avoid handling stale errors from disabled state)
    if (!statusPollingEnabled) return;

    // Handle errors from the transaction status API
    if (transactionStatusError) {
      isTransactionStatusCheckedRef.current = true;
      newWindowRef.current?.close();
      newWindowRef.current = null;
      emitProcessingError(t('aurumErrors.unknown'));
      return;
    }

    if (transactionStatusData) {
      if (isTransactionStatusSuccessState(transactionStatusData.state)) {
        isTransactionStatusCheckedRef.current = true;
        closeNewTab();
        resetRedirectionCheckData();
        onSuccess(transactionStatusData);
      } else if (isTransactionStatusFailureState(transactionStatusData.state)) {
        isTransactionStatusCheckedRef.current = true;
        closeNewTab();
        resetRedirectionCheckData();
        emitProcessingError(translateAurumErrorKey(transactionStatusData.errorKey, t), transactionStatusData);
      }
    }
  }, [transactionStatusData, transactionStatusError, statusPollingEnabled, emitProcessingError, onSuccess, t, resetRedirectionCheckData, closeNewTab]);

  const openNewTab = () => {
    try {
      if (typeof window !== 'undefined' && typeof window.open === 'function') {
        // Open a blank tab synchronously to avoid popup blockers
        newWindowRef.current = window.open('about:blank', '_blank');
        if (!newWindowRef.current) {
          debug.forceError('window.open returned null (popup blocked?)');
        } else {
          // Inject loading content into the new tab
          try {
            const loadingMessage = t('redirection_step.provider_window_loading_message');
            const loadingContent = getLoadingWindowContent(loadingMessage);
            newWindowRef.current.document.write(loadingContent);
            newWindowRef.current.document.close();
          } catch (error) {
            debug.forceError('Error writing loading content to new window:', (error as Error)?.message || error);
          }
        }
      } else {
        debug.forceError('window.open is not available in this environment');
      }
    } catch (error) {
      debug.forceError('Error opening new window:', (error as Error)?.message || error);
    }
  };

  const resetFlow = () => {
    isRedirectionCheckedRef.current = false;
    isTransactionStatusCheckedRef.current = false;
    setLastResolvedDepositType(undefined);
    setCompoundId(null);
    setTransactionRedirectionCheckError(null);
    resetRedirectionCheckData();
    closeNewTab();
    createDepositTransactionMutation.reset();
  };

  const resolvedDepositTypeFromStatus: 'FTD' | 'SD' | undefined =
    transactionStatusData != null && typeof transactionStatusData.isFirstPayIn === 'boolean' ? (transactionStatusData.isFirstPayIn ? 'FTD' : 'SD') : undefined;

  useEffect(() => {
    if (resolvedDepositTypeFromStatus !== undefined) {
      setLastResolvedDepositType(resolvedDepositTypeFromStatus);
    }
  }, [resolvedDepositTypeFromStatus]);

  const depositType: 'FTD' | 'SD' | undefined = resolvedDepositTypeFromStatus ?? lastResolvedDepositType;

  return {
    createDepositTransactionMutation,
    transactionRedirectionCheckData: transactionRedirectionCheckDataRef.current,
    transactionRedirectionCheckHeaders,
    transactionRedirectionCheckStatus,
    compoundId,
    isTransactionInProgress: !isTransactionStatusCheckedRef.current,
    isRedirectionInProgress: !!transactionRedirectionCheckDataRef.current?.payload?.redirection?.url,
    transactionStatusData,
    depositType,
    resetFlow,
    newWindowRef,
    openNewTab,
    closeNewTab,
  };
}

export { getPayInErrorMessage } from '../../utils/aurumErrorTranslation';
export { useDepositFlow };
export type { UseDepositFlowOptions, UseDepositFlowReturn };
